import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import TopicCard from "@/components/feature/TopicCard";
import { categories } from "@/mocks/site";
import { topics } from "@/mocks/topics";
import { useSeo } from "@/hooks/useSeo";
import type { Topic } from "@/types";

const allTopics: Topic[] = topics;

const categoryFilters = [{ id: "all", name: "全部话题", icon: "ri-apps-2-line" }, ...categories];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: "话题广场 - 黑料专区",
      description: "按分类浏览全部吃瓜话题，支持关键词搜索与分类切换。",
      inLanguage: "zh-CN",
      url: "https://leiysp.cn/topics",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://leiysp.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "话题广场",
          item: "https://leiysp.cn/topics",
        },
      ],
    },
    {
      "@type": "ItemList",
      itemListElement: allTopics.map((topic, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: topic.title,
        url: `https://leiysp.cn/topic/${topic.id}`,
      })),
    },
  ],
};

export default function TopicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: "话题广场 - 吃瓜话题分类浏览 | 黑料专区",
    description:
      "黑料专区话题广场汇集全部吃瓜话题，支持按吃瓜话题、热点事件、圈内动态、瓜友观点分类浏览，也可用关键词快速搜索热议话题。",
    keywords: "吃瓜话题,黑料专区,话题广场,瓜友讨论,吃瓜热议,吃瓜交流",
    path: "/topics",
    ogType: "website",
    jsonLd,
  });

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return allTopics.filter((topic) => {
      const matchCategory = activeCategory === "all" || topic.categoryId === activeCategory;
      const matchKeyword =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.excerpt.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, query]);

  const updateCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setSearchParams(next);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = keyword.trim();
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="transition-colors hover:text-primary-600">
                    首页
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li className="text-foreground-800">话题广场</li>
              </ol>
            </nav>

            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              话题广场
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              这里是黑料专区的话题集散地。按分类浏览，或用关键词搜索你感兴趣的热议话题，
              每一张卡片点开都是一个完整的讨论现场。
            </p>

            <form onSubmit={handleSearch} className="mt-6 flex max-w-xl flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-500"></i>
                <input
                  type="text"
                  name="keyword"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索话题、标签或关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
          <div className="flex flex-wrap gap-2">
            {categoryFilters.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => updateCategory(cat.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-medium text-primary-700">{filtered.length}</span> 个话题
              {query && (
                <>
                  {" ，关键词："}
                  <span className="font-medium text-foreground-900">「{query}」</span>
                </>
              )}
            </p>
            {(query || activeCategory !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams());
                }}
                className="flex items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-accent-600"
              >
                <i className="ri-close-line"></i>
                清除筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                暂时没有匹配的话题
              </p>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或者直接浏览全部话题。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams());
                }}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览全部话题
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}